import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReflectionPage({ token }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(false);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const fetchDailyPrompt = useCallback(async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/reflections/daily-prompt`, config);
      setPrompt(res.data.prompt);
    } catch (error) {
      console.error('Failed to fetch prompt');
    }
  }, [token]);

  const fetchReflections = useCallback(async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/reflections`, config);
      setReflections(res.data);
    } catch (error) {
      console.error('Failed to fetch reflections');
    }
  }, [token]);

  useEffect(() => {
    fetchDailyPrompt();
    fetchReflections();
  }, [fetchDailyPrompt, fetchReflections]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!response.trim()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/reflections`, { prompt, response }, axiosConfig);
      toast.success(t('toast.reflection.saved'));
      setResponse('');
      fetchDailyPrompt();
      fetchReflections();
    } catch (error) {
      toast.error(t('toast.reflection.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="reflection-page">
      <div className="relative">
        <img
          src="https://images.unsplash.com/photo-1543183601-e4c9648a72f3?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Zen Stone"
          className="absolute inset-0 w-full h-64 sm:h-96 object-cover opacity-10"
        />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-12">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6 sm:mb-8 rounded-full text-sm"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('nav.back')}
        </Button>

        <div className="text-center mb-8 sm:mb-12 space-y-3 sm:space-y-4">
          <h1 className="font-serif text-3xl sm:text-5xl font-light tracking-tight" data-testid="reflection-title">
            {t('reflection.title')}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
            {t('reflection.subtitle')}
          </p>
        </div>

        {/* Today's Prompt */}
        <div className="glass-card rounded-2xl p-6 sm:p-12 mb-8 sm:mb-12 border border-border shadow-xl" data-testid="reflection-prompt-card">
          <p className="font-serif text-lg sm:text-2xl text-center text-foreground/90 mb-6 sm:mb-8 leading-relaxed" data-testid="daily-prompt">
            {prompt}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <Textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder={t('reflection.placeholder')}
              className="min-h-[150px] sm:min-h-[200px] font-serif text-base sm:text-lg bg-transparent border-none focus:ring-0 resize-none"
              data-testid="reflection-response-input"
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={loading || !response.trim()}
                className="rounded-full px-6 sm:px-8 text-sm sm:text-base"
                data-testid="save-reflection-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? t('reflection.saving') : t('reflection.save')}
              </Button>
            </div>
          </form>
        </div>

        {/* Past Reflections */}
        <div className="space-y-4 sm:space-y-6" data-testid="past-reflections">
          <h2 className="font-serif text-2xl sm:text-3xl font-light text-center mb-6 sm:mb-8">{t('reflection.past')}</h2>
          {reflections.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm sm:text-base">{t('reflection.empty')}</p>
          ) : (
            reflections.map((ref) => (
              <div
                key={ref.id}
                className="bg-card/50 backdrop-blur-sm rounded-xl p-5 sm:p-8 border border-border hover:shadow-lg transition-all duration-300"
                data-testid={`reflection-item-${ref.id}`}
              >
                <p className="font-serif text-base sm:text-lg text-muted-foreground mb-3 sm:mb-4 italic">"{ref.prompt}"</p>
                <p className="font-serif text-sm sm:text-base text-foreground leading-relaxed">{ref.response}</p>
                <p className="text-xs text-muted-foreground mt-3 sm:mt-4">
                  {new Date(ref.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
